import {zapi} from '../__internal__/http-client/index.js';

import * as endpoints from './endpoints.js';

/**
 * Same as "getPowerGuide" but the response is in a slightly different format.
 *
 * @param {$ZAPI.EPG.PowerGuideRequest} data
 * @returns {Promise<$ZAPI.EPG.GuideResponse>}
 */
export const getEPG = ({luhash, ...data}) => zapi({
    path: `v3/cached/${luhash}/guide`,
    method: 'get',
    endpointID: endpoints.EPG_RECEIVED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.2.3+-+Channels+and+EPG+Data#2.2.3-ChannelsandEPGData-powerguide
 * @param {$ZAPI.EPG.PowerGuideRequest} data
 * @returns {Promise<$ZAPI.EPG.PowerGuideResponse>}
 */
export const getPowerGuide = ({luhash, ...data}) => zapi({
    path: `v2/cached/program/power_guide/${luhash}`,
    method: 'get',
    endpointID: endpoints.EPG_POWER_GUIDE_RECEIVED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.2.3+-+Channels+and+EPG+Data#2.2.3-ChannelsandEPGData-powerdetails
 * @param {$ZAPI.EPG.PowerDetailsRequest} data
 * @returns {Promise<$ZAPI.EPG.PowerDetailsResponse>}
 */
export const getPowerDetails = ({pghash, ...data}) => zapi({
    path: `v2/cached/program/power_details/${pghash}`,
    method: 'get',
    endpointID: endpoints.EPG_POWER_DETAILS_RECEIVED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.2.3+-+Channels+and+EPG+Data#2.2.3-ChannelsandEPGData-search
 * @param {$ZAPI.EPG.SearchRequest} data
 * @returns {Promise<$ZAPI.EPG.SearchResponse>}
 */
export const searchEPG = (data) => zapi({
    path: 'program/search',
    method: 'get',
    endpointID: endpoints.EPG_SEARCH_COMPLETED,
    data,
});

/**
 * @param {$ZAPI.EPG.SearchRequest} data
 * @returns {Promise<$ZAPI.EPG.GroupedSearchResponse>}
 */
export const searchGroupedEPG = (data) => zapi({
    path: 'program/search',
    method: 'get',
    endpointID: endpoints.EPG_GROUPED_SEARCH_COMPLETED,
    data: {
        group: true,
        ...data,
    },
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.2.3+-+Channels+and+EPG+Data#2.2.3-ChannelsandEPGData-getseries
 * @param {$ZAPI.EPG.SeriesAllSeasonsRequest | $ZAPI.EPG.SeriesSeasonRequest} data
 * @returns {Promise<$ZAPI.EPG.SeriesResponse>}
 */
const getSeries = ({filters, ...restData}) => zapi({
    path: 'series',
    method: 'get',
    endpointID: endpoints.EPG_SERIES_DETAILS_RECEIVED,
    data: {
        ...restData,
        ...filters,
    },
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.2.3+-+Channels+and+EPG+Data#2.2.3-ChannelsandEPGData-getseries
 * @param {$ZAPI.EPG.SeriesAllSeasonsRequest} data
 * @returns {Promise<$ZAPI.EPG.SeriesResponse>}
 */
export const getSeriesAllSeasons = (data) => getSeries(data);

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.2.3+-+Channels+and+EPG+Data#2.2.3-ChannelsandEPGData-getseries
 * @param {$ZAPI.EPG.SeriesSeasonRequest} data
 * @returns {Promise<$ZAPI.EPG.SeriesResponse>}
 */
export const getSeriesSeason = (data) => getSeries(data);

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.2.3+-+Channels+and+EPG+Data#2.2.3-ChannelsandEPGData-getsimilar
 * @param {$ZAPI.EPG.SimilarProgramsRequest} data
 * @returns {Promise<$ZAPI.EPG.SimilarProgramsResponse>}
 */
export const getSimilarPrograms = (data) => zapi({
    path: 'recommendations/similar',
    method: 'get',
    endpointID: endpoints.EPG_SIMILAR_PROGRAMS_RECEIVED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.2.3+-+Channels+and+EPG+Data#2.2.3-ChannelsandEPGData-getconnectedcontents
 * @param {$ZAPI.EPG.ConnectedContentsRequest} data
 * @returns {Promise<$ZAPI.EPG.ConnectedContentResponse>}
 */
export const getEPGConnectedContents = ({pghash}) => zapi({
    path: `cached/${pghash}/program/connected_contents`,
    method: 'get',
    endpointID: endpoints.EPG_CONNECTED_CONTENTS_RECEIVED,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.2.3+-+Channels+and+EPG+Data#2.2.3-ChannelsandEPGData-getbroadcaststates
 * @returns {Promise<$ZAPI.EPG.BroadcastStatusesResponse>}
 */
export const getBroadcastStatuses = () => zapi({
    path: 'broadcast/status',
    method: 'get',
    endpointID: endpoints.EPG_STATUSES_RECEIVED,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.2.3+-+Channels+and+EPG+Data#2.2.3-ChannelsandEPGData-updatebroadcaststateposition
 * @param {$ZAPI.EPG.UpdateBroadcastPositionRequest} data
 * @returns {Promise<$ZAPI.EPG.UpdateBroadcastPositionResponse>}
 */
export const updateBroadcastPosition = (data) => zapi({
    path: 'broadcast/status/update_position',
    method: 'post',
    endpointID: endpoints.EPG_POSITION_UPDATED,
    data: {
        ...data,
        position: new Date(data.position * 1000).toISOString(),
    },
});
